/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {getPathWithLocale} from '@salesforce/retail-react-app/app/utils/url';
import {
  Box, Button, Divider, Drawer, DrawerBody, DrawerContent, DrawerFooter,
  DrawerOverlay, Flex, Popover, PopoverBody, PopoverContent, PopoverTrigger,
  Radio, RadioGroup, Text, useDisclosure, useMultiStyleConfig, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {GlobalIcon, GlobalMegaMenuIcon} from '@amblique/king-living/app/components/icons';
import {getLanguageCode, getLocaleDisplayName} from '@amblique/king-living/app/components/locale-text';
import {useLocaleSync} from '@amblique/king-living/app/hooks/use-locale-sync';

/** Trigger button with globe icon and language code (forwardRef for Popover positioning) */
const TriggerButton = React.forwardRef(({localeId, styles, ariaLabel, variant = 'desktop', ...props}, ref) => {
  const GlobeIcon = variant === 'mobile' ? GlobalMegaMenuIcon : GlobalIcon;

  return (
    <Button ref={ref} aria-label={ariaLabel} {...styles.button} {...props}>
      <GlobeIcon {...styles.icon} />
      <Text {...styles.localeText}>{getLanguageCode(localeId)}</Text>
    </Button>
  );
});

TriggerButton.displayName = 'TriggerButton';

TriggerButton.propTypes = {
  localeId: PropTypes.string.isRequired,
  styles: PropTypes.object.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['desktop', 'mobile']),
};

/** Locale list item with divider */
const LocaleItem = ({localeId, index, children}) => (
  <React.Fragment key={localeId}>
    {index > 0 && <Divider m={0} />}
    {children}
  </React.Fragment>
);

LocaleItem.propTypes = {
  localeId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Locale Selector Component
 *
 * Displays a globe icon with locale code for switching between available locales.
 * - 2 locales: Toggle button
 * - 3+ locales: Popover (desktop) or bottom Drawer with Radio (mobile)
 *
 * Persistence:
 * - Saves locale preference to cookie (365 days) for guest users
 * - Syncs to SFCC customer account (c_preferredLocale) for registered users
 *
 * @param {string} variant - 'desktop' (default) or 'mobile'
 */
const LocaleSelector = ({variant = 'desktop', ...props}) => {
  const styles = useMultiStyleConfig('LocaleSelector');
  const intl = useIntl();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {site, buildUrl} = useMultiSite();
  const {setPreferredLocale} = useLocaleSync();

  const locales = site?.l10n?.supportedLocales?.map((l) => l.id) || [];
  const currentLocale = intl.locale;

  if (locales.length <= 1) return null;

  const ariaLabel = intl.formatMessage({
    id: 'header.locale_selector.assistive_msg',
    defaultMessage: 'Select Language',
  });

  const handleChange = async (localeId) => {
    // Only place we update cookie from user action (not from URL). Also syncs to account if logged in.
    // Must await so SCAPI update completes before redirect (KINDW-743).
    await setPreferredLocale(localeId);

    window.location = getPathWithLocale(localeId, buildUrl, {disallowParams: ['refine']});
  };

  // 2-locale toggle
  if (locales.length === 2) {
    const otherLocale = locales.find((id) => id !== currentLocale);

    return (
      <Box {...styles.container} {...props}>
        <TriggerButton
          ariaLabel={ariaLabel}
          localeId={otherLocale}
          styles={styles}
          variant={variant}
          onClick={() => handleChange(otherLocale)}
        />
      </Box>
    );
  }

  // 3+ locales - Desktop Popover
  if (variant === 'desktop') {
    return (
      <Box {...styles.container} {...props}>
        <Popover placement="bottom-end">
          <PopoverTrigger>
            <TriggerButton ariaLabel={ariaLabel} localeId={currentLocale} styles={styles} variant="desktop" />
          </PopoverTrigger>
          <PopoverContent {...styles.popoverContent}>
            <PopoverBody p={0}>
              <VStack align="stretch" spacing={0}>
                {locales.map((localeId, index) => (
                  <LocaleItem key={localeId} index={index} localeId={localeId}>
                    <Box
                      as="button"
                      cursor="pointer"
                      textAlign="left"
                      width="100%"
                      onClick={() => handleChange(localeId)}
                      {...styles.popoverItem}
                      {...(localeId === currentLocale && styles.popoverItemActive)}
                    >
                      <Text {...styles.popoverItemText}>
                        {getLocaleDisplayName(localeId)}
                      </Text>
                    </Box>
                  </LocaleItem>
                ))}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>
    );
  }

  // 3+ locales - Mobile Drawer
  return (
    <Box {...styles.container} {...props}>
      <TriggerButton
        ariaLabel={ariaLabel}
        localeId={currentLocale}
        styles={styles}
        variant="mobile"
        onClick={onOpen}
      />
      <Drawer data-testid="locale-selector-drawer" isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay {...styles.drawerOverlay} />
        <DrawerContent {...styles.drawerContent}>
          <DrawerBody {...styles.drawerBody}>
            <RadioGroup value={currentLocale}>
              <VStack align="stretch" spacing={0}>
                {locales.map((localeId, index) => (
                  <LocaleItem key={localeId} index={index} localeId={localeId}>
                    <Flex
                      align="center"
                      justify="space-between"
                      onClick={() => {
                        handleChange(localeId);
                        onClose();
                      }}
                      {...styles.radioItem}
                      {...(localeId === currentLocale && styles.radioItemActive)}
                    >
                      <Text {...styles.radioItemText}>
                        {getLocaleDisplayName(localeId)}
                      </Text>
                      <Radio pointerEvents="none" value={localeId} />
                    </Flex>
                  </LocaleItem>
                ))}
              </VStack>
            </RadioGroup>
          </DrawerBody>
          <DrawerFooter {...styles.drawerFooter}>
            <Button variant="ghost" width="100%" onClick={onClose} {...styles.cancelButton}>
              {intl.formatMessage({id: 'locale_selector.cancel', defaultMessage: 'Cancel'})}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

LocaleSelector.displayName = 'LocaleSelector';

LocaleSelector.propTypes = {
  /** 'desktop' (Popover) or 'mobile' (bottom Drawer with Radio) */
  variant: PropTypes.oneOf(['desktop', 'mobile']),
};

export default LocaleSelector;
